import React, { useEffect, useState } from "react";

import { subscriberContext } from "../context/subscriberContext.tsx";
import { API_URLS } from "../utils/apiConfig.ts";
import { extractCountryCodeFromPhone, DEFAULT_COUNTRY_CODE } from "../utils/countryUtils.ts";

export const SubscriberProvider = ({ children = <></> }) => {
  const [subscriberDetails, setSubscriberDetails] = useState(null);
  const [isError, setIsError] = useState(false);
  const [plans, setPlans] = useState([]);
  const [isSubscriberDetailsFetching, setIsSubscriberDetailsFetching] =
    useState(false);
  const [isplansFetching, setIsPlansFetching] = useState(false);
  
  const urlParams = new URLSearchParams(window.location.search);
  const urlCountry = urlParams.get('country');

  const getTokenFromPath = () => {
    const match = window.location.pathname.match(/^\/watch-list\/([^/]+)\/?$/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  const resolveToken = async (pathToken?: string | null) => {
    const endpoint = pathToken
      ? `${API_URLS.TOKEN}?token=${encodeURIComponent(pathToken)}`
      : API_URLS.TOKEN;

    try {
      const response = await fetch(endpoint, { method: "GET" });
      if (!response.ok) {
        return null;
      }

      const authorizationHeader = response.headers.get("Authorization");
      if (authorizationHeader) {
        return authorizationHeader.replace("Bearer ", "").trim();
      }
      return null;      
    } catch (error) {
      console.error("Error resolving token from backend", error);
      return null;
    }
  };

  const invalidateSession = () => {
    localStorage.removeItem("tokn");
    setSubscriberDetails(null);
    setIsError(true);
  };

  const fetchSubscriberDetails = async (tokn?: string) => {
    if ((subscriberDetails || isSubscriberDetailsFetching) && !tokn) return;
    // random access token from path if available
    const pathToken = getTokenFromPath();
    let token = tokn;
    if (!token && pathToken) {
      token = await resolveToken(pathToken);
    }
    if (!token) {
      token = localStorage.getItem("tokn");
    }
    if (!token) {
      setIsError(true);
      return Promise.resolve();
    }
    localStorage.setItem("tokn", token);
    setIsSubscriberDetailsFetching(true);
    try {
      const resp = await fetch(API_URLS.SUBSCRIBER, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (resp.status === 401 || resp.status === 403) {
        invalidateSession();
        return;
      }
      const res = await resp.json();
      if (!resp.ok || !res?.phoneNumber) {
        invalidateSession();
        return;
      }
      setSubscriberDetails(res);
      setIsError(false);

      const detectedCountryCode = extractCountryCodeFromPhone(res.phoneNumber);
      fetchSubscriberPlansByCountry(detectedCountryCode);
    } catch (err) {
      setIsError(true);
    } finally {
      setIsSubscriberDetailsFetching(false);
    }
  };

  const fetchSubscriberPlans = () => {
    if (plans.length || isplansFetching) return;
    // Fetch plans data
    setIsPlansFetching(true);
    fetchPlansByCountry(DEFAULT_COUNTRY_CODE)
      .then((data) => {
        setPlans(data);
      })
      .catch((error) => {
        console.error("Error fetching plans:", error);
      })
      .finally(() => {
        setIsPlansFetching(false);
      });
  };

  const fetchSubscriberPlansByCountry = (countryCode: string) => {
    if (isplansFetching) return;
    setIsPlansFetching(true);
    fetchPlansByCountry(countryCode)
      .then((data) => {
        setPlans(data);
      })
      .catch((error) => {
        console.error("Error fetching country-specific plans:", error);
        return fetchPlansByCountry(DEFAULT_COUNTRY_CODE);
      })
      .then((fallbackData) => {
        if (fallbackData) {
          setPlans(fallbackData);
        }
      })
      .finally(() => {
        setIsPlansFetching(false);
      });
  };

  const logout = () => {
    localStorage.removeItem("tokn");
    setSubscriberDetails(null);
  };

  const fetchPlans = async () => {
    try {
      const response = await fetch(API_URLS.PLANS);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching plans:", error);
      return [];
    }
  };

  const fetchPlansByCountry = async (countryCode: string) => {
    try {
      const response = await fetch(API_URLS.PLANS_BY_COUNTRY(countryCode));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error fetching plans for country ${countryCode}:`, error);
      return fetchPlans();
    }
  };

  useEffect(() => {
    fetchSubscriberDetails();
    
    const initialCountry = urlCountry || DEFAULT_COUNTRY_CODE;
    if (initialCountry !== DEFAULT_COUNTRY_CODE) {
      fetchSubscriberPlansByCountry(initialCountry);
    } else {
      fetchSubscriberPlans();
    }
  }, []);

  return (
    <subscriberContext.Provider
      value={{
        subscriberDetails,
        fetchSubscriberDetails,
        setSubscriberDetails,
        isSubscriberDetailsFetching,
        logout,
        isError,
        plans,
        setIsError,
        fetchSubscriberPlansByCountry
      }}
    >
      {children}
    </subscriberContext.Provider>
  );
};
